import React, { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils";
import {
  AiChatState,
  InputRequirement,
  SURVEY_DECLINED_MESSAGE,
  UserInput,
  UserInputAnswer,
  UserInput as UserInputType,
} from "@intelligems/ig-types";
import { AppDispatch, useDispatch } from "@src/store";
import { getAiChat, handleAiChatAction } from "@src/store/slices/aiChat";

import { ExecutionPanelProps } from "../ExecutionPanel";
import {
  ExecutionPanelContext,
  ExecutionPanelContextValue,
  ExecutionPanelSurvey,
} from "./ExecutionPanelContext";
import { recordSurveyAnswer } from "./surveyAskState";
import {
  DropZoneRef,
  ExecutionPanelSlotProps,
  ExecutionPanelSlots,
  FileUpload,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { IgAiIcon } from "@intelligems/ig-theme";
import { TextRevealProvider } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/TextRevealContext";

type ExecutionPanelProviderProps = ExecutionPanelProps & {
  dropZoneRef?: DropZoneRef;
  fileUpload?: FileUpload;
  children: ReactNode;
};

/**
 * Owns all of an ExecutionPanel's data handling. Receives `ExecutionPanelProps`
 * untouched, resolves the derived state (live-vs-historical chat, streaming,
 * submit, …), and exposes `props + derived` to descendants via context. The
 * panel's render body and its sub-components consume `useExecutionPanelContext`
 * instead of being prop-drilled.
 */
export const ExecutionPanelProvider = (props: ExecutionPanelProviderProps) => {
  const { children, ...rest } = props;
  const liveAiChat = useSelector(getAiChat);
  const dispatch = useDispatch();
  const [debugMode, setDebugMode] = useImmer(false);
  const [userInputMessage, setUserInputMessage] = useImmer<UserInput | null>(
    null
  );
  // Plain state, not `useImmer` — the value is a DOM node, which immer's
  // auto-freeze has no business touching.
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const aiChat = props.injectedChatState || liveAiChat;

  const streaming = isStreaming(props, aiChat);
  const workflowDone = isWorkflowDone(aiChat);
  const disabled = isDisabled(props, streaming);

  const inputRequirement = getInputRequirement(aiChat);
  const surveyConfig =
    inputRequirement.userInput.type === "survey"
      ? inputRequirement.userInput
      : null;

  // Stepper state for a parked survey. Keyed on the requirement's identity
  // so a NEW survey re-seeds from
  // question one with no answers, while re-renders of the same one keep the
  // user's place.
  const [surveyState, setSurveyState] = useImmer<{
    seededFor: object | null;
    currentIndex: number;
    answers: Record<number, UserInputAnswer>;
  }>({ seededFor: null, currentIndex: 0, answers: {} });
  if (surveyConfig && surveyState.seededFor !== inputRequirement) {
    setSurveyState(() => ({
      seededFor: inputRequirement,
      currentIndex: 0,
      answers: {},
    }));
  }

  const answerCurrent = (answer: UserInputAnswer) => {
    if (!surveyConfig) {
      return;
    }
    const outcome = recordSurveyAnswer(
      surveyConfig.questions,
      surveyState.answers,
      surveyState.currentIndex,
      answer
    );
    if (outcome.submission) {
      // That was the last unanswered question — the whole set submits as one
      // input, and the state resets for whatever the agent parks next.
      onSubmit(context, dispatch, outcome.submission);
      setSurveyState(() => ({ seededFor: null, currentIndex: 0, answers: {} }));
    } else {
      setSurveyState((draft) => {
        draft.answers = outcome.answers;
        draft.currentIndex = outcome.currentIndex;
      });
    }
  };

  const survey: ExecutionPanelSurvey | null = surveyConfig
    ? {
        questions: surveyConfig.questions,
        currentIndex: surveyState.currentIndex,
        answers: surveyState.answers,
        goTo: (index) =>
          setSurveyState((draft) => {
            draft.currentIndex = index;
          }),
        answerCurrent,
        dismiss: () =>
          onSubmit(context, dispatch, {
            type: "text",
            content: SURVEY_DECLINED_MESSAGE,
          }),
      }
    : null;

  const context: ExecutionPanelContext = {
    ...rest,
    title: rest.title ?? (
      <>
        <IgAiIcon sx={{ marginRight: 2 }} shine />
        AI Assistant
      </>
    ),
    closeLabel: rest.closeLabel ?? "Close AI chat",
    aiChat,
    streaming,
    workflowDone,
    disabled,
    compact: props.layoutDensity === "compact",
    onSubmit: () => onSubmit(context, dispatch),
    submitInput: (input: UserInput) => onSubmit(context, dispatch, input),
    debugMode,
    setDebugMode,
    menuAnchorEl,
    openMenu: setMenuAnchorEl,
    closeMenu: () => setMenuAnchorEl(null),
    inputRequirement,
    survey,
    userInputMessage,
    setUserInputMessage,
    getSlot: (key, defaultComponent) => getSlot(props, key, defaultComponent),
    getSlotProps: (key) => getSlotProps(props, key),
    renderToolContent: props.renderToolContent,
    dropZoneRef: props.dropZoneRef,
    fileUpload: props.fileUpload,
  };

  return (
    <ExecutionPanelContextValue.Provider value={context}>
      {/*
        Wraps the transcript AND the composer, which is the whole point: the
        suggestion strip in the composer has to be able to see that a bubble in
        the transcript is still revealing.
      */}
      <TextRevealProvider>{children}</TextRevealProvider>
    </ExecutionPanelContextValue.Provider>
  );
};

const DEFAULT_TEXT_INPUT: InputRequirement = {
  type: "user_input",
  variablePath: "userInput",
  prompt: "",
  userInput: { type: "text" },
};

function onSubmit(
  context: ExecutionPanelContext,
  dispatch: AppDispatch,
  overrideInput?: UserInputType
) {
  const { aiChat, onSend, workflowId, version, setUserInputMessage } = context;

  // Submission is gated here rather than by disabling the textarea, so the
  // user can always keep typing (e.g. while the AI is streaming or an upload
  // is settling). The composer's Enter-to-submit calls this directly, bypassing
  // the disabled submit button, so the guard must live at this layer.
  if (context.disabled) {
    return;
  }

  // `overrideInput` submits a specific value directly (e.g. a clicked choice
  // suggestion) instead of reading the composer's draft — the draft state
  // update wouldn't be visible in this same tick.
  const input = overrideInput ?? prepareSubmit(context);
  if (!input) {
    return;
  }

  // Free text typed while a survey is parked answers the CURRENT question
  // instead of resuming the workflow with a stray text turn — `answerCurrent`
  // advances the stepper, and comes back through here with an override input
  // once the last question is answered. Attachments fall through: a survey
  // answer has nowhere to carry files, so a message with uploads submits as a
  // normal turn rather than silently dropping them.
  if (
    !overrideInput &&
    context.survey &&
    input.type === "text" &&
    input.content &&
    !input.files?.length
  ) {
    const { questions, currentIndex } = context.survey;
    context.survey.answerCurrent({
      prompt: questions[currentIndex].prompt,
      choices: [],
      freeText: input.content,
    });
    setUserInputMessage(null);
    return;
  }

  if (aiChat.sessionId) {
    void onSend.chat({ sessionId: aiChat.sessionId, input });
  } else {
    const sessionId = v4();
    const nodeId = v4();

    dispatch(
      handleAiChatAction([
        {
          type: "initialize",
          workflowId: WORKFLOW_IDS.UI_ACTIONS,
          sessionId,
          nodeId,
        },
      ])
    );

    void onSend.chat({
      workflowId,
      version,
      sessionId,
      input,
    });
  }

  setUserInputMessage(null);
}

function prepareSubmit(
  context: ExecutionPanelContext
): UserInputType | undefined {
  const { fileUpload, userInputMessage } = context;

  if (!fileUpload) {
    return userInputMessage ?? undefined;
  }
  if (fileUpload.hasUploadsInProgress || fileUpload.hasErrors) {
    return;
  }
  const files =
    fileUpload.successfulFiles.length > 0
      ? fileUpload.successfulFiles
      : undefined;
  if (!userInputMessage && !files) {
    return;
  }

  fileUpload?.clearUploads();

  switch (userInputMessage?.type) {
    case undefined:
      return { type: "text", content: "", files };
    case "text":
      return {
        type: "text",
        content: userInputMessage.content,
        files,
      };
    default:
      return userInputMessage ?? undefined;
  }
}

function getSlot<K extends keyof ExecutionPanelSlots>(
  props: ExecutionPanelProps,
  key: K,
  defaultComponent?: ExecutionPanelSlots[K]
): ExecutionPanelSlots[K] {
  const slot = props.slots?.[key];
  return slot !== undefined ? slot : defaultComponent;
}

/**
 * A replaced slot is an already-built node, so there is nothing to hand props
 * to — returning them anyway would let a caller set both and quietly wonder why
 * only one took effect. Defaults get their props; overrides get nothing.
 *
 * Not every `slotProps` key is a slot: `assistantMessage` and `userMessage`
 * configure renderers that nothing can replace today (that is what the part
 * registry is for), so the keys are a superset rather than a mirror. The lookup
 * walks entries instead of indexing, which stays type-safe for the keys that do
 * overlap and is a harmless miss for the ones that do not.
 */
function getSlotProps<K extends keyof ExecutionPanelSlotProps>(
  props: ExecutionPanelProps,
  key: K
): ExecutionPanelSlotProps[K] | undefined {
  const overridden = Object.entries(props.slots ?? {}).some(
    ([slotKey, node]) => slotKey === key && node !== undefined
  );
  if (overridden) {
    return undefined;
  }
  return props.slotProps?.[key];
}

function getInputRequirement(aiChat: AiChatState) {
  return aiChat.nextAction && aiChat.nextAction.type === "user_input"
    ? aiChat.nextAction
    : DEFAULT_TEXT_INPUT;
}

function isWorkflowDone(aiChat: AiChatState) {
  return (
    aiChat.status === "completed" ||
    aiChat.status === "waiting_for_input" ||
    aiChat.status === "error"
  );
}

function isStreaming(props: ExecutionPanelProps, aiChat: AiChatState) {
  return (
    !props.injectedChatState &&
    (aiChat.status === "active" ||
      aiChat.status === "processing" ||
      props.onSend.isPending)
  );
}

function isDisabled(props: ExecutionPanelProviderProps, streaming: boolean) {
  const { onSend, fileUpload } = props;

  const disabled = onSend.isPending || onSend.disabled || streaming;
  if (!fileUpload) {
    return disabled;
  }

  return disabled || fileUpload.hasUploadsInProgress || fileUpload.hasErrors;
}
